import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Image } from './Image'
import { Option } from './Option'
import { RecordDataMap } from './RecordDataMap'
import { FKType } from './SQLBuilder'
import { User } from './User'

type ReaderConstructorArgs = ConstructorArgs<() => Reader>

export enum READER_FIELD {
    READER_FIELD_TYPE,
    READER_FIELD_LABEL,
    READER_FIELD_SERIAL_NUMBER,
}

export class Reader extends BaseRecord {
    readonly recordType = 'reader'

    type?: string
    reader?: string
    label?: string
    line1?: string
    line2?: string
    city?: string
    state?: string
    postalCode?: string
    serialNumber?: string
    registrationCode?: string
    isUpdated?: boolean
    image?: Image

    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment
    registeredDate?: moment.Moment

    readonly stripeReader?: RecordDataMap

    constructor(data?: Partial<ReaderConstructorArgs>) {
        super(data)
        if (!data) {
            this.owner = PSApplication.me()
            return
        }

        this.type = data.type ?? undefined
        this.serialNumber = data.serial_number ?? undefined
        this.label = data.label ?? undefined
        this.reader = data.reader ?? undefined
        this.line1 = data.line1 ?? undefined
        this.line2 = data.line2 ?? undefined
        this.postalCode = data.postal_code ?? undefined
        this.city = data.city ?? undefined
        this.state = data.state ?? undefined
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.serialNumber = data.serial_number ?? undefined
        this.registrationCode = data.registration_code ?? undefined
        this.registeredDate = this.propertyFromTimestampArg(data.registered_date)
        this.isUpdated = data.is_updated ?? false
        this.stripeReader = this.propertyFromDictionaryArg(data.stripe_reader)
        this.image = this.propertyFromImageArg(data.image, data.image_uri)
    }

    clone(overrides?: Partial<ReaderConstructorArgs>): Reader {
        return new Reader({ ...this.serializeForClone(), ...overrides })
    }

    get isInternetReader(): boolean {
        return this.type === 'bbpos_wisee'
    }

    get requiresRegistration(): boolean {
        return this.isInternetReader && !this.registeredDate
    }

    static readerTypeStrings(includeInternetReaders: boolean): Option[] {
        const strings: Option[] = []
        strings.push(
            new Option(undefined, {
                id: 'bbpos_chipper2x',
                label: this.longType('bbpos_chipper2x'),
            })
        )
        strings.push(new Option(undefined, { id: 'stripe_m2', label: this.longType('stripe_m2') }))
        if (includeInternetReaders)
            strings.push(
                new Option(undefined, { id: 'bbpos_wisee', label: this.longType('bbpos_wisee') })
            )
        return strings
    }

    static longType(type: string | undefined): string {
        if (type === 'bbpos_chipper2x') return 'BBPOS Chipper 2X BT'
        else if (type === 'stripe_m2') return 'Stripe Reader M2'
        else if (type === 'bbpos_wisee') return 'WISE POS e'
        // fallback to displaying latest reader type when we don't know what was shipped
        if (!type) return 'Stripe Reader M2'
        return type
    }

    get longType(): string {
        return Reader.longType(this.type)
    }

    get location(): string {
        const blocks: string[] = []
        const streetAddress = this.streetAddress
        const cityAndState = this.cityState
        if (streetAddress) {
            blocks.push(streetAddress)
            if (cityAndState) blocks.push(cityAndState)
        }
        else if (cityAndState) blocks.push(cityAndState)
        if (this.postalCode) blocks.push(this.postalCode.trim())
        return blocks.join(', ')
    }

    private get cityState(): string {
        const blocks: string[] = []
        if (this.city) blocks.push(this.city.trim())
        if (this.state) blocks.push(this.state.trim())
        return blocks.join(', ')
    }

    private get streetAddress(): string {
        const blocks: string[] = []
        if (this.line1) blocks.push(this.line1.trim())
        if (this.line2) blocks.push(this.line2.trim())
        return blocks.join(', ')
    }

    jsonObject(): any {
        const jsonData = super.jsonObject()
        jsonData['serial_number'] = this.serialNumber
        jsonData['type'] = this.type
        jsonData['line1'] = this.line1
        jsonData['line2'] = this.line2
        jsonData['city'] = this.city
        jsonData['state'] = this.state
        jsonData['reader'] = this.reader
        jsonData['postal_code'] = this.postalCode
        if (this.label) jsonData['label'] = this.label
        if (this.id) jsonData['id'] = this.id
        if (this.registrationCode) jsonData['registration_code'] = this.registrationCode
        return jsonData
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
        if (data.registeredDate) this.registeredDate = moment(data.registeredDate)
    }

    static fk: FKType = {
        users: ['owner'],
    }

    static dataType(): DataType {
        return 'readers'
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
}

export const readerStatus = (r: Reader) => {
    if (r.requiresRegistration) {
        return 'Registration required'
    }
    return `${r.isUpdated ? 'Up to date' : 'Update required'}`
}
